import React from 'react';

function UserStats({ stats }) {
  const getPercentage = (value, total) => {
    if (!total) return 0;
    return Math.round((value / total) * 100);
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.total || 0,
      icon: 'fas fa-users',
      color: 'primary',
      description: 'All registered users'
    },
    {
      title: 'Active Users',
      value: stats.active || 0,
      icon: 'fas fa-user-check',
      color: 'success',
      description: 'Users who can login',
      percentage: getPercentage(stats.active, stats.total)
    },
    {
      title: 'Recent Logins',
      value: stats.recent_logins || 0,
      icon: 'fas fa-sign-in-alt',
      color: 'info',
      description: 'Logins in last 7 days',
      percentage: getPercentage(stats.recent_logins, stats.active)
    },
    {
      title: 'Inactive Users',
      value: (stats.total || 0) - (stats.active || 0),
      icon: 'fas fa-user-times',
      color: 'warning',
      description: 'Disabled accounts',
      percentage: getPercentage((stats.total || 0) - (stats.active || 0), stats.total)
    }
  ];

  const roleStats = [
    {
      role: 'admin',
      label: 'Administrators',
      value: stats.admins || 0,
      icon: 'fas fa-user-shield',
      color: 'danger',
      description: 'Full system access'
    },
    {
      role: 'editor',
      label: 'Editors',
      value: stats.editors || 0,
      icon: 'fas fa-user-edit',
      color: 'primary',
      description: 'Content management'
    },
    {
      role: 'viewer',
      label: 'Viewers',
      value: stats.viewers || 0,
      icon: 'fas fa-user-check',
      color: 'secondary',
      description: 'Read-only access'
    }
  ];

  return (
    <div className="user-stats">
      {/* Overview Statistics */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="section-title">
            <i className="fas fa-chart-bar me-2"></i>
            User Overview
          </h4>
        </div>
      </div>

      <div className="row mb-5">
        {statCards.map((stat, index) => (
          <div key={index} className="col-lg-3 col-md-6 mb-4">
            <div className={`stat-card stat-card-${stat.color}`}>
              <div className="stat-card-body">
                <div className="stat-icon">
                  <i className={stat.icon}></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-title">{stat.title}</div>
                  <div className="stat-description">{stat.description}</div>
                  {stat.percentage !== undefined && (
                    <div className="stat-percentage">
                      {stat.percentage}% of total
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Role Distribution */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="section-title">
            <i className="fas fa-user-cog me-2"></i>
            Role Distribution
          </h4>
        </div>
      </div>

      <div className="row mb-5">
        {roleStats.map((role, index) => (
          <div key={index} className="col-lg-4 col-md-6 mb-4">
            <div className={`role-card role-card-${role.color}`}>
              <div className="role-card-header">
                <div className="role-icon">
                  <i className={role.icon}></i>
                </div>
                <div className="role-info">
                  <div className="role-label">{role.label}</div>
                  <div className="role-description">{role.description}</div>
                </div>
              </div>
              <div className="role-card-body">
                <div className="role-value">{role.value}</div>
                <div className="role-percentage">
                  {getPercentage(role.value, stats.total)}% of all users
                </div>
                {/* Progress bar */}
                <div className="progress mt-2">
                  <div 
                    className={`progress-bar bg-${role.color}`}
                    role="progressbar"
                    style={{ width: `${getPercentage(role.value, stats.total)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Activity Summary */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="section-title">
            <i className="fas fa-activity me-2"></i>
            Activity Summary
          </h4>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="activity-card">
            <div className="activity-card-body">
              <h5 className="activity-title">User Activity Insights</h5>
              <div className="activity-insights">
                <div className="insight-item">
                  <div className="insight-icon bg-success">
                    <i className="fas fa-thumbs-up"></i>
                  </div>
                  <div className="insight-content">
                    <div className="insight-value">
                      {getPercentage(stats.active, stats.total)}%
                    </div>
                    <div className="insight-label">Active User Rate</div>
                    <div className="insight-description">
                      Percentage of users who can access the system
                    </div>
                  </div>
                </div>

                <div className="insight-item">
                  <div className="insight-icon bg-info">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="insight-content">
                    <div className="insight-value">
                      {getPercentage(stats.recent_logins, stats.active)}%
                    </div>
                    <div className="insight-label">Recent Activity</div>
                    <div className="insight-description">
                      Active users who logged in within the last 7 days
                    </div>
                  </div>
                </div>

                <div className="insight-item">
                  <div className="insight-icon bg-primary">
                    <i className="fas fa-user-shield"></i>
                  </div>
                  <div className="insight-content">
                    <div className="insight-value">
                      {getPercentage(stats.admins, stats.total)}%
                    </div>
                    <div className="insight-label">Admin Users</div>
                    <div className="insight-description">
                      Percentage of users with administrative privileges
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="summary-card">
            <div className="summary-card-body">
              <h5 className="summary-title">Quick Summary</h5>
              <div className="summary-items">
                <div className="summary-item">
                  <span className="summary-label">Total Accounts:</span>
                  <span className="summary-value">{stats.total || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Active Accounts:</span>
                  <span className="summary-value text-success">{stats.active || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Inactive Accounts:</span>
                  <span className="summary-value text-warning">
                    {(stats.total || 0) - (stats.active || 0)}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Recent Logins:</span>
                  <span className="summary-value text-info">{stats.recent_logins || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .user-stats {
          padding: 1rem 0;
        }

        .section-title {
          color: #495057;
          font-weight: 600;
          margin-bottom: 1.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e9ecef;
        }

        .stat-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          height: 100%;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
        }

        .stat-card-body {
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          flex-shrink: 0;
        }

        .stat-card-primary .stat-icon { background: linear-gradient(135deg, #007bff, #0056b3); }
        .stat-card-success .stat-icon { background: linear-gradient(135deg, #28a745, #1e7e34); }
        .stat-card-info .stat-icon { background: linear-gradient(135deg, #17a2b8, #117a8b); }
        .stat-card-warning .stat-icon { background: linear-gradient(135deg, #ffc107, #e0a800); }
        .stat-card-danger .stat-icon { background: linear-gradient(135deg, #dc3545, #bd2130); }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #495057;
          line-height: 1;
          margin-bottom: 0.25rem;
        }

        .stat-title {
          font-size: 1rem;
          font-weight: 600;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .stat-description {
          font-size: 0.875rem;
          color: #8e9ba8;
          margin-bottom: 0.25rem;
        }

        .stat-percentage {
          font-size: 0.8rem;
          color: #6c757d;
          font-weight: 500;
        }

        .role-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: transform 0.3s ease;
          height: 100%;
        }

        .role-card:hover {
          transform: translateY(-3px);
        }

        .role-card-header {
          padding: 1.25rem 1.5rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .role-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          color: white;
          flex-shrink: 0;
        }

        .role-card-primary .role-icon { background: #007bff; }
        .role-card-success .role-icon { background: #28a745; }
        .role-card-info .role-icon { background: #17a2b8; }
        .role-card-warning .role-icon { background: #ffc107; }
        .role-card-danger .role-icon { background: #dc3545; }
        .role-card-secondary .role-icon { background: #6c757d; }

        .role-info {
          flex: 1;
        }

        .role-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.2rem;
        }

        .role-description {
          font-size: 0.875rem;
          color: #6c757d;
        }

        .role-card-body {
          padding: 1.5rem;
          text-align: center;
        }

        .role-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #495057;
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        .role-percentage {
          font-size: 0.9rem;
          color: #6c757d;
          margin-bottom: 0.5rem;
        }

        .progress {
          height: 6px;
          background-color: #e9ecef;
          border-radius: 3px;
        }

        .progress-bar {
          border-radius: 3px;
          transition: width 0.6s ease;
        }

        .activity-card,
        .summary-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .activity-card-body,
        .summary-card-body {
          padding: 1.5rem;
        }

        .activity-title,
        .summary-title {
          color: #495057;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .activity-insights {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .insight-item {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .insight-icon {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .insight-content {
          flex: 1;
        }

        .insight-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #495057;
          line-height: 1;
          margin-bottom: 0.25rem;
        }

        .insight-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.25rem;
        }

        .insight-description {
          font-size: 0.875rem;
          color: #6c757d;
        }

        .summary-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e9ecef;
        }

        .summary-item:last-child {
          border-bottom: none;
        }

        .summary-label {
          font-weight: 500;
          color: #6c757d;
        }

        .summary-value {
          font-weight: 600;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .activity-insights {
            gap: 1rem;
          }
          
          .insight-item {
            flex-direction: column;
            text-align: center;
            gap: 0.5rem;
          }
          
          .stat-card-body {
            flex-direction: column;
            text-align: center;
            gap: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
}

export default UserStats;